import {Component, EventEmitter, Input, OnChanges, Output} from '@angular/core';
import {ContenitoreInfo, FaseInfo, PercorsoRiga, PosizionePercorso, RuoloRiga} from '../../../_models/ContenitoreInfo';
import {VistaPercorso} from '../../../_models/AccountOrganizzazione';
import {StatoEnum} from '../../../_models/generated/stato-enum';
import {AzioneCard, CardFase, GruppoFasi, instradaAzione, RigaPercorsoView} from './contenitore-percorso.model';

const POSIZIONI: Record<PosizionePercorso, [string, string]> = {
  [PosizionePercorso.DaFare]: ['da-fare', 'Da fare'],
  [PosizionePercorso.Passata]: ['passata', '✓ Passata'],
  [PosizionePercorso.Attuale]: ['attuale', 'Attuale'],
  [PosizionePercorso.Alternativa]: ['alternativa', 'Alternativa'],
  [PosizionePercorso.Prossima]: ['prossima', 'Prossima'],
  [PosizionePercorso.FuoriPercorso]: ['fuori', 'Fuori percorso']
};

const RUOLI: Record<RuoloRiga, string> = {
  [RuoloRiga.Contenitore]: 'riga del contenitore',
  [RuoloRiga.Valle]: 'a valle',
  [RuoloRiga.Altra]: 'altra riga'
};

/**
 * Feature 166 «Percorso fasi»: prepara righe e card una sola volta (ngOnChanges) e le passa alla vista
 * scelta nelle opzioni operatore. Le viste sono solo presentazione.
 */
@Component({
  selector: 'app-contenitore-percorso',
  templateUrl: './contenitore-percorso.component.html',
  standalone: false
})
export class ContenitorePercorsoComponent implements OnChanges {
  @Input() info: ContenitoreInfo | null = null;
  @Input() vista: VistaPercorso = VistaPercorso.Orizzontale;
  // Truthy solo dall'interfaccia manuale: abilita i bottoni APRI/LANCIA.
  @Input() azioni: unknown = null;
  @Input() storia = false;
  @Input() chiudiFasiPrecedenti = false;
  @Output() apri = new EventEmitter<FaseInfo>();
  @Output() cerca = new EventEmitter<FaseInfo>();
  @Output() storiaFase = new EventEmitter<FaseInfo>();
  // Vista Mappa: fase scelta per il footer di Info contenitore (all'inizio la prossima); null nelle altre viste.
  @Output() selezione = new EventEmitter<CardFase | null>();
  righe: RigaPercorsoView[] = [];
  selezionata: CardFase | null = null;
  protected readonly Vista = VistaPercorso;

  ngOnChanges() {
    const percorso = this.info?.percorso ?? [];
    this.righe = percorso.map((riga, i) => this.toRiga(riga, percorso[i - 1]));
    this.selezionata = this.vista === VistaPercorso.Mappa ? this.selezioneIniziale() : null;
    // Il footer è nel template del chiamante, già controllato quando arriva ngOnChanges: emissione al giro dopo
    // (sincrona darebbe ExpressionChangedAfterItHasBeenChecked).
    const iniziale = this.selezionata;
    void Promise.resolve().then(() => this.selezione.emit(iniziale));
  }

  onAzione(card: CardFase) {
    instradaAzione(card, f => this.apri.emit(f), f => this.cerca.emit(f));
  }

  seleziona(card: CardFase | null) {
    this.selezionata = card;
    this.selezione.emit(card);
  }

  // La prima prossima (pronta per APRI/LANCIA), altrimenti l'attuale, altrimenti la prima fase.
  private selezioneIniziale(): CardFase | null {
    const cards = this.righe.flatMap(r => r.cards);
    return cards.find(c => c.classe === 'prossima') ?? cards.find(c => c.classe === 'attuale') ?? cards[0] ?? null;
  }

  private toRiga(riga: PercorsoRiga, precedente?: PercorsoRiga): RigaPercorsoView {
    const cards = riga.fasi.map(f => this.toCard(f));
    return {
      riga,
      chiave: `${riga.idDocumento}-${riga.idRiga}`,
      etichettaRuolo: RUOLI[riga.ruolo] ?? '',
      frecciaPrima: precedente && riga.ruolo === RuoloRiga.Valle ? this.freccia(riga) : null,
      primaAltra: riga.ruolo === RuoloRiga.Altra && precedente?.ruolo !== RuoloRiga.Altra,
      cards,
      gruppi: this.gruppi(cards)
    };
  }

  private freccia(riga: PercorsoRiga): string {
    return riga.numFaseConsumo != null
      ? `consumato in distinta alla fase ${riga.numFaseConsumo}`
      : 'consumato in distinta';
  }

  private gruppi(cards: CardFase[]): GruppoFasi[] {
    const gruppi: GruppoFasi[] = [];
    cards.forEach(card => {
      const numero = Math.floor(card.fase.numFase);
      const ultimo = gruppi[gruppi.length - 1];
      if (ultimo?.numero === numero) {
        ultimo.cards.push(card);
      } else {
        gruppi.push({numero, classe: card.classe, cards: [card]});
      }
    });
    return gruppi;
  }

  private toCard(fase: FaseInfo): CardFase {
    const [classe, etichetta] = POSIZIONI[fase.posizione ?? PosizionePercorso.DaFare];
    const nota = fase.posizione === PosizionePercorso.Attuale || fase.posizione === PosizionePercorso.Prossima;
    const azione = this.azione(fase, nota);
    return {
      fase,
      chiave: `${fase.idDocumento}-${fase.idRiga}-${fase.idRigaLavorazione}`,
      classe,
      etichetta: fase.posizione === PosizionePercorso.Attuale && this.info?.attualeInConsumo
        ? `${etichetta} · in consumo` : etichetta,
      stato: [fase.stato != null ? StatoEnum[fase.stato] : '', fase.flgTerminato ? 'Terminata' : '']
        .filter(s => !!s).join(' · '),
      azione,
      testoAzione: azione === 'lancia' ? 'LANCIA ORDINE' : 'APRI ORDINE',
      abilitata: azione === 'cerca' || (nota && fase.puoAprire),
      motivo: azione !== 'cerca' && azione !== 'nessuno' && !fase.puoAprire ? fase.motivo : undefined,
      precedentiAperte: nota && this.azioni && this.chiudiFasiPrecedenti ? fase.fasiPrecedentiAperte : 0
    };
  }

  // Attuale e prossime: apribilità già calcolata dal servizio; le altre fasi: ricerca al tocco (D2).
  private azione(fase: FaseInfo, nota: boolean): AzioneCard {
    if (!this.azioni || fase.flgTerminato) {
      return 'nessuno';
    }
    if (!nota) {
      return 'cerca';
    }
    return fase.giaLanciata ? 'apri' : 'lancia';
  }
}
